import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
// Migrated 1:1 from @nexato/nx-core-module (issue/components/dueDateTime/dueDateTime-widget.component.ts, #683)
import { Issue } from '../../../entities/issue/issue';
import { CommandService } from '../../../services/command/command.service';
import { CommandInput, CommandTemplate } from '../../../entities/commandTemplate/commandTemplate';
import { IssueInput } from '../../../entities/issue/issueInput';
import { EditDateTimePeriodInputV2Component } from '../../edit-dueDateTime-form-control-v2/edit-dueDateTime-form-control-v2.component';
import { IssueWidget } from '../../../services/issue/issue-widget.interface';
import { WidgetCoordinatorService } from '../../../services/issue/widget-coordinator.service';
import { RefetchCoordinator } from '../../../services/refetch-coordinator/refetch-coordinator.service';

@Component({
  selector: 'nx-issue-dueDateTime-widget',
  templateUrl: './dueDateTime-widget.component.html',
})
export class IssueDueDateTimeWidgetComponent implements OnDestroy, OnInit, OnChanges, IssueWidget {

  @Input() issue: Issue;
  @ViewChild('dateTimeComponent') dateTimeComponent: EditDateTimePeriodInputV2Component;

  public editMode: boolean = false;
  public isLoading: boolean = false;

  public commandTemplate: CommandTemplate;
  private initialDueDateTime: string | null = null;

  dueDateTimeControl = new FormControl<string | null>(null);
  form = new FormGroup({
    dueDateTime: this.dueDateTimeControl
  });

  private widgetId = `dueDateTime-widget-${Math.random().toString(36).substr(2, 9)}`;
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private refetchCoordinator = inject(RefetchCoordinator);
  private unregisterRefetch?: () => void;

  constructor(
    private commandService: CommandService,
    private coordinator: WidgetCoordinatorService
  ) {
  }


  ngOnInit(): void {
    // Register with RefetchCoordinator as detail view
    if (this.issue?.id) {
      this.unregisterRefetch = this.refetchCoordinator.register(
        this,
        this.issue.getEntityType(),
        this.issue.id
      );
    }

    // Subscribe to cancel all requests
    this.coordinator.cancelAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(exceptId => {
        if (exceptId !== this.widgetId) {
          this.cancel();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['issue'] && this.issue && this.issue.id) {
      this.loadCommandTemplate();
      this.initialDueDateTime = this.issue?.dueDateTime ?? null;
      this.dueDateTimeControl.setValue(this.issue?.dueDateTime);
    }
  }

  loadCommandTemplate() {
    const commandInput = new CommandInput();
    commandInput.name = "issueSetDueDateTimeCommand";
    commandInput.addParameter('commandProviderId', this.issue.id);

    this.commandService.getCommandByCommandInput(commandInput)
        .pipe(take(1))
        .subscribe(result => {
          this.commandTemplate = result.data.getCommandByCommandInput;
          this.isLoading = false;
          this.cdr.detectChanges();
        });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  edit() {
    this.coordinator.requestCancelAll(this.widgetId);
    this.editMode = true;
  }

  save() {
    // Wenn keine Änderung vorliegt, nur Edit-Mode beenden
    if (!this.hasChanges()) {
      this.editMode = false;
      return;
    }

    // Bei Änderung: Backend-Update durchführen
    this.editMode = false;
    const issueInput = new IssueInput();
    issueInput.id = this.issue?.id;
    issueInput.dueDateTime = this.form.value?.dueDateTime;
    let commandInput = new CommandInput();
    commandInput.name = this.commandTemplate.name;
    commandInput.addParameter('commandProviderId', this.issue.id);
    commandInput.addParameter('issueInput', issueInput);
    this.isLoading = true;
    this.commandService.sendCommand(commandInput, (result) => {
      this.loadCommandTemplate();
      this.refetchCoordinator.emit({ entityType: this.issue.getEntityType(), entityId: this.issue.id });
    });
  }

  cancel() {
    this.editMode = false;
  }

  hasChanges(): boolean {
    const currentValue = this.dueDateTimeControl.value ?? null;
    const initialValue = this.initialDueDateTime ?? null;

    // Beide null oder undefined → keine Änderung
    if (currentValue === null && initialValue === null) {
      return false;
    }

    // Einer null, der andere nicht → Änderung
    if (currentValue === null || initialValue === null) {
      return true;
    }

    // Vergleiche als Date-Objekte um Format-Unterschiede zu ignorieren
    const currentDate = new Date(currentValue).getTime();
    const initialDate = new Date(initialValue).getTime();

    return currentDate !== initialDate;
  }

  reload(): void {
    this.loadCommandTemplate();
  }

}
